import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ImageUp, Plus, Trash2 } from "lucide-react";
import { api, assetUrl, errMsg } from "@/lib/api";
import { shrinkImage } from "@/lib/images";
import { PARTNERS_QUERY, partnerHeight, usePartners } from "@/hooks/usePartners";

const inp = "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-2.5 text-cream outline-none focus:border-terra-soft placeholder:text-cream/40";
const iconBtn = "grid place-items-center h-9 w-9 rounded-full border border-cream/20 text-cream/80 hover:bg-cream hover:text-ink transition-colors disabled:opacity-30";
const SIZES = [["xs", "Extra small"], ["sm", "Small"], ["md", "Medium"], ["lg", "Large"]];

const uploadLogo = async (file) => {
  const fd = new FormData();
  fd.append("file", await shrinkImage(file));
  return (await api.post("/admin/uploads", fd)).data.url;
};

function useRun() {
  const qc = useQueryClient();
  return async (fn, ok) => {
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: PARTNERS_QUERY });
      if (ok) toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
}

function SizeSelect({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${inp} sm:w-40`} data-testid={testId} aria-label="Logo size">
      {SIZES.map(([v, l]) => <option key={v} value={v} className="text-ink">{l}</option>)}
    </select>
  );
}

function AddPartner() {
  const run = useRun();
  const [name, setName] = useState("");
  const [size, setSize] = useState("md");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a logo image first");
    setBusy(true);
    await run(async () => {
      const logo = await uploadLogo(file);
      await api.post("/admin/partners", { name: name.trim(), logo, size });
      setName(""); setFile(null); e.target.reset();
    }, "Partner added");
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border border-cream/10 bg-cream/5 p-5 grid gap-4 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end" data-testid="add-partner-form">
      <label><span className="eyebrow text-cream/70 block mb-2">Partner name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. MakeMyTrip" className={inp} data-testid="add-partner-name-input" /></label>
      <label><span className="eyebrow text-cream/70 block mb-2">Logo (JPG, PNG or WEBP)</span>
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className={`${inp} file:mr-3 file:rounded-full file:border-0 file:bg-cream file:px-3 file:py-1 file:text-ink`} data-testid="add-partner-logo-input" /></label>
      <label><span className="eyebrow text-cream/70 block mb-2">Size</span><SizeSelect value={size} onChange={setSize} testId="add-partner-size-select" /></label>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-terra px-5 py-2.5 font-semibold text-paper hover:bg-terra-soft disabled:opacity-60" data-testid="add-partner-submit-btn">
        <Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add partner"}
      </button>
    </form>
  );
}

function PartnerRow({ p, prev, next }) {
  const run = useRun();
  const file = useRef(null);
  const [name, setName] = useState(p.name);
  const body = { name: p.name, logo: p.logo, size: p.size, order: p.order };
  const save = (changes, ok) => run(() => api.put(`/admin/partners/${p.id}`, { ...body, ...changes }), ok);
  const swap = (o) => run(async () => {
    await api.put(`/admin/partners/${p.id}`, { ...body, order: o.order });
    await api.put(`/admin/partners/${o.id}`, { name: o.name, logo: o.logo, size: o.size, order: p.order === o.order ? p.order + 1 : p.order });
  });
  const replace = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) await run(async () => api.put(`/admin/partners/${p.id}`, { ...body, logo: await uploadLogo(f) }), "Logo replaced");
  };
  const id = p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <li className="rounded-2xl border border-cream/10 bg-cream/5 p-4 grid gap-4 md:grid-cols-[13rem_1fr_auto] md:items-center" data-testid={`partner-row-${id}`}>
      <div className="h-20 rounded-xl bg-white grid place-items-center px-4">
        <img src={assetUrl(p.logo)} alt={`${p.name} logo`} className={`${partnerHeight(p.size)} w-auto max-w-full object-contain`} />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== p.name && save({ name: name.trim() }, "Name saved")}
          className={inp} aria-label="Partner name" data-testid={`partner-name-input-${id}`} />
        <SizeSelect value={p.size} onChange={(size) => save({ size })} testId={`partner-size-select-${id}`} />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => swap(prev)} disabled={!prev} className={iconBtn} aria-label="Move left" data-testid={`partner-up-${id}`}><ArrowUp className="h-4 w-4" /></button>
        <button onClick={() => swap(next)} disabled={!next} className={iconBtn} aria-label="Move right" data-testid={`partner-down-${id}`}><ArrowDown className="h-4 w-4" /></button>
        <button onClick={() => file.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-3.5 py-2 text-sm hover:bg-cream hover:text-ink transition-colors" data-testid={`partner-replace-${id}`}>
          <ImageUp className="h-4 w-4" /> Swap logo
        </button>
        <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={replace} data-testid={`partner-replace-input-${id}`} />
        <button onClick={() => window.confirm(`Remove ${p.name}?`) && run(() => api.delete(`/admin/partners/${p.id}`), "Partner removed")}
          className={`${iconBtn} hover:!bg-red-500 hover:!text-white`} aria-label="Delete partner" data-testid={`partner-delete-${id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export function PartnersManager() {
  const partners = usePartners();
  return (
    <div className="space-y-6" data-testid="partners-manager">
      <p className="text-cream/70 max-w-2xl">These logos glide across the home page in this order. Add new partners, swap a logo, change its size or remove it.</p>
      <AddPartner />
      <ul className="space-y-3">
        {partners.map((p, i) => <PartnerRow key={`${p.id}-${p.name}-${p.size}-${p.logo}`} p={p} prev={partners[i - 1]} next={partners[i + 1]} />)}
      </ul>
    </div>
  );
}
